import React, { useState } from 'react'
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  message,
  Tag,
  Select,
  Spin,
  Icon,
  Tooltip
} from 'antd'
import { attributes } from '../../helper/data.character'
import {
  createCharacter,
  getTaskStatus
} from '../../../../../../services/api/aiVideoGeneration'
import { usePresignedUpload } from '../../../../hooks/usePresignedUpload'
import './AddCharacterModal.scss'

const FormItem = Form.Item
const Option = Select.Option

const AddCharacterModal = ({ form, onCreate, onClose: onCancel, videoId }) => {
  const [image, setImage] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [selectedAttributes, setSelectedAttributes] = useState([])
  const [isCreatingCharacter, setIsCreatingCharacter] = useState(false)

  const { uploadFiles } = usePresignedUpload({ videoId })

  const handleImageUpload = file => {
    const isImage =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg' ||
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')

    if (!isImage) {
      message.error(
        'You can only upload PNG, JPG, JPEG and iPhone (HEIC/HEIF) image files!'
      )
      return Upload.LIST_IGNORE
    }

    setImageFile(file)
    const reader = new FileReader()
    reader.onload = function(e) {
      setImage(e.target.result)
    }
    reader.readAsDataURL(file)
    return false
  }

  const handleRemoveImage = e => {
    e.stopPropagation()
    setImage(null)
    setImageFile(null)
  }

  // Helper function to extract apparel items from selected attributes
  const extractApparelItems = () => {
    const apparelAttributes = selectedAttributes.filter(
      attr =>
        attr.attribute.toLowerCase().includes('apparel') ||
        attr.attribute.toLowerCase().includes('clothing') ||
        attr.attribute.toLowerCase().includes('outfit')
    )
    return apparelAttributes.map(attr => attr.option)
  }

  // Helper function to get gender from selected attributes
  const getGender = () => {
    const genderAttr = selectedAttributes.find(
      attr =>
        attr.attribute.toLowerCase().includes('gender') ||
        attr.attribute.toLowerCase() === 'sex'
    )
    return genderAttr ? genderAttr.option.toLowerCase() : 'male' // default to male
  }

  const handleSubmit = async () => {
    try {
      const fieldsError = await new Promise(resolve => {
        form.validateFields((err, values) => {
          resolve({ err, values })
        })
      })

      if (fieldsError.err) {
        message.error('Please fill in all required fields!')
        return
      }

      const { values } = fieldsError

      // Either image or image prompt is required
      if (!image && !values.imagePrompt) {
        message.error('Please upload an image or provide an image prompt!')
        return
      }

      setIsCreatingCharacter(true)

      let uploadedImageUrl = null
      if (imageFile) {
        const uploadResult = await uploadFiles({
          files: imageFile,
          category: 'image',
          folder: 'character-references'
        })

        if (uploadResult.success && uploadResult.urls.length > 0) {
          uploadedImageUrl = uploadResult.urls[0]
        } else {
          throw new Error('Failed to upload character image.')
        }
      }

      const payload = {
        gender: getGender(),
        apparel_items: extractApparelItems(),
        original_prompt: values.imagePrompt || null,
        character_tag: null,
        character_name: values.name,
        reference_image_uri: uploadedImageUrl || null,
        platform: 'runway',
        video_id: videoId
      }

      const response = await createCharacter(payload)
      const taskId = response && response.data && response.data.task_id
      await waitForVideoTaskSuccess(taskId)

      message.success('Character created successfully!')

      if (onCreate) {
        onCreate({
          ...values,
          image: uploadedImageUrl || image,
          attributes: selectedAttributes,
          imageSource: uploadedImageUrl || image,
          imagePrompt: values.imagePrompt,
          apiResponse: response && response.data
        })
      }

      // Reset form state
      form.resetFields()
      setImage(null)
      setImageFile(null)
      setSelectedAttributes([])

      // Close the modal
      onCancel()
    } catch (error) {
      console.error('Error creating character:', error)
      message.error(
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
          'Failed to create character. Please try again.'
      )
    } finally {
      setIsCreatingCharacter(false)
    }
  }

  async function waitForVideoTaskSuccess(taskId) {
    try {
      if (!taskId) return null

      const maxAttempts = 60
      const delayMs = 10000

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const statusResponse = await getTaskStatus(taskId)
          const data =
            statusResponse && statusResponse.data ? statusResponse.data : null
          const status = data && data.status ? data.status : null

          if (status === 'success') {
            return data && data.output ? data.output : ''
          }

          if (status === 'failed' || status === 'error') {
            return null
          }
        } catch (err) {
          if (attempt === maxAttempts - 1) return null
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
      return null
    } catch (err) {
      return null
    }
  }

  const handleAttributeChange = (attributeName, option) => {
    const attributeObj = attributes.find(attr => attr.name === attributeName)
    if (!attributeObj) return

    const filteredAttributes = selectedAttributes.filter(
      attr => attr.attribute !== attributeName
    )

    if (!option) {
      setSelectedAttributes(filteredAttributes)
      return
    }

    setSelectedAttributes([
      ...filteredAttributes,
      {
        attribute: attributeName,
        option: option,
        label: attributeObj.label
      }
    ])
  }

  return (
    <Modal
      visible={true}
      title={null} // Custom header handled via CSS or we can keep it standard
      onOk={handleSubmit}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isCreatingCharacter}
          onClick={handleSubmit}
        >
          Create Character
        </Button>
      ]}
      width={900}
      centered
      className="add-character-modal"
      closable={!isCreatingCharacter}
      maskClosable={!isCreatingCharacter}
    >
      <div className="ant-modal-header">
        <div className="modal-title">
          <Icon type="user-add" /> Create New Character
        </div>
      </div>

      <Spin spinning={isCreatingCharacter} tip="Creating character...">
        <div className="add-character-layout">
          {/* LEFT COLUMN: Image Upload */}
          <div className="layout-left">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              disabled={isCreatingCharacter}
              accept=".png,.jpg,.jpeg,.heic,.heif"
              className="image-uploader-component"
            >
              <div
                className={`image-upload-wrapper ${image ? 'has-image' : ''}`}
              >
                {image ? (
                  <>
                    <img src={image} alt="Preview" className="image-preview" />
                    <Tooltip title="Remove Image">
                      <button
                        className="remove-image-btn"
                        onClick={handleRemoveImage}
                      >
                        <Icon type="close" />
                      </button>
                    </Tooltip>
                  </>
                ) : (
                  <div className="upload-placeholder">
                    <Icon type="cloud-upload" className="upload-icon" />
                    <div className="upload-text">Upload Reference Image</div>
                    <div className="upload-hint">Click to browse</div>
                  </div>
                )}
              </div>
            </Upload>

            <div className="upload-helper-text">
              <p>Upload a clear portrait image for best results.</p>
              <div style={{ opacity: 0.6 }}>
                Supported Formats: PNG, JPG, JPEG, HEIC, HEIF
              </div>
              <div style={{ opacity: 0.6, marginTop: 4 }}>
                Recommended Size: 3:4 Aspect Ratio
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Form Details */}
          <div className="layout-right">
            <div
              style={{
                padding: '10px 14px',
                backgroundColor: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#0050b3',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Icon type="info-circle" theme="filled" />
              <span>
                <strong>Required:</strong> Character name and either an image
                upload OR image prompt.
              </span>
            </div>

            <Form layout="vertical">
              <div className="form-section-title">Basic Information</div>

              <FormItem label="Character Name" required>
                {form.getFieldDecorator('name', {
                  rules: [
                    {
                      required: true,
                      message: 'Please enter the character name!'
                    }
                  ]
                })(
                  <Input
                    placeholder="e.g. John Doe"
                    disabled={isCreatingCharacter}
                  />
                )}
              </FormItem>

              <FormItem label="Image Prompt (Description)">
                <Tooltip title="Describe the character's facial features, hair, and style if you are not providing a reference image, or to refine the generation.">
                  {form.getFieldDecorator('imagePrompt')(
                    <Input.TextArea
                      placeholder="e.g. A young man with short brown hair, wearing a blue suit..."
                      rows={3}
                      disabled={isCreatingCharacter}
                    />
                  )}
                </Tooltip>
              </FormItem>

              <div className="form-section-title" style={{ marginTop: 24 }}>
                Physical Attributes
              </div>

              <div className="attributes-grid">
                {attributes.map(attr => (
                  <FormItem
                    key={attr.name}
                    label={attr.label}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="Select..."
                      onChange={value =>
                        handleAttributeChange(attr.name, value)
                      }
                      disabled={isCreatingCharacter}
                      allowClear
                      value={
                        selectedAttributes.find(a => a.attribute === attr.name)
                          ? selectedAttributes.find(
                              a => a.attribute === attr.name
                            ).option
                          : undefined
                      }
                    >
                      {attr.options.map(option => (
                        <Option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </Option>
                      ))}
                    </Select>
                  </FormItem>
                ))}
              </div>
            </Form>
          </div>
        </div>
      </Spin>
    </Modal>
  )
}

export default Form.create()(AddCharacterModal)
